/**
 * Turning an API refusal into a sentence, for the quote and sales-order screens.
 *
 * ===========================================================================
 * THE SERVER'S OWN MESSAGE IS THE FALLBACK, NOT THE LAST RESORT
 * ===========================================================================
 * `peekErrorText` (`features/inquiries/errors.ts` — relocated in WT5 when its
 * original home, `PeekPanel.tsx`, died with the slide-over kill) sets the house shape:
 * translate the two or three codes a screen can act on, and otherwise return
 * `error.message` verbatim. That is deliberate and it matters more here than
 * anywhere else in the app, because the quote pricing path's refusals are
 * WRITTEN TO BE READ — `quote-pricing.service.ts` answers things like "One or
 * more products have no price for this country. Set it with
 * PUT /countries/:cc/products before quoting them." Replacing that with a
 * generic "Could not price the quote" would throw away the only sentence that
 * tells the operator what to do.
 *
 * So this file translates exactly the cases where the API's wording is about
 * mechanism rather than about the user's next step, and passes everything else
 * through.
 */

import { ApiRequestError, ApiTransportError } from '../../lib/api';
import { saftFieldsSentence } from '../../domain/saft-fields';

/*
 * A `NOT_IMPLEMENTED_PENDING_DECISION` BRANCH USED TO LIVE HERE, and it is gone
 * with the rule it served. `isPricingNotImplemented` existed so the builder could
 * render OQ-1's 501 — a non-`flat` product — as a survivable state rather than a
 * failure. The Phase 3a close-out voids OQ-1: the operator supplies the rate and
 * the pricing service raises no 501 at all. A predicate that can never be true is
 * worse than no predicate, because the next reader believes the state exists.
 *
 * `POST /quotes/:id/discount-decision` DOES still answer 501 (an approved AMOUNT
 * has no column to land in — OQ-4, blocked on a migration). That refusal reaches
 * the screen through `quoteErrorText` below, which passes the API's own sentence
 * through, and it is the discount panel's message rather than the builder's.
 */

/*
 * ⚠ THE `PRICE_UNVERIFIED` REWRITE BRANCH IS GONE, AND `refusalReason` WENT WITH IT
 * (tax-groups design §5/§6, G1 — rule 5). The one 422 this file ever rewrote existed
 * because the API's refusal ended by pointing at `GET /admin/verify-flags`, an API
 * path, and the web swapped it for the go-live screen. Both halves of that are
 * retired: the price gate no longer exists (the server cannot produce the reason),
 * and the go-live screen the rewrite pointed at is itself removed (OD-5). The
 * surviving lifecycle refusals — including NAME_UNVERIFIED, which OD-4 keeps — name
 * a SCREEN in the server's own words, so they pass through verbatim below.
 */

/** `details.reason` of a lifecycle 422, or `null` when the body carries none. */
function refusalReasonOf(details: unknown): string | null {
  if (typeof details !== 'object' || details === null) return null;
  const reason = (details as { reason?: unknown }).reason;
  return typeof reason === 'string' ? reason : null;
}

/** The two instants of a DE 74/19 33 c) refusal (ISO strings, as the server sent them). */
export interface ClockBehind {
  at: string;
  last: string;
}

/**
 * AGT U2, 33 c): a send or confirm the server refused 409 `CLOCK_BEHIND_LAST_DOCUMENT` — its
 * clock reads earlier than the country's last document. The screens ask the operator to confirm
 * the clock and resend with `confirm_clock: true`; `null` for any other error.
 */
export function clockBehindOf(error: unknown): ClockBehind | null {
  if (!(error instanceof ApiRequestError) || error.status !== 409) return null;
  if (refusalReasonOf(error.details) !== 'CLOCK_BEHIND_LAST_DOCUMENT') return null;
  const details = error.details as { system_entry_at?: unknown; last_system_entry_at?: unknown };
  return { at: String(details.system_entry_at ?? ''), last: String(details.last_system_entry_at ?? '') };
}

/** 33 c) in plain words — the question the confirmation dialog asks. */
export function clockBehindSentence(clock: ClockBehind): string {
  const when = (iso: string): string => {
    const date = new Date(iso);
    return Number.isFinite(date.getTime()) ? date.toLocaleString('en-GB') : 'an unknown time';
  };
  return (
    `The server's clock reads ${when(clock.at)}, which is earlier than the last document ` +
    `issued in this country (${when(clock.last)}). Check that the date and time are correct; ` +
    'if they are, confirm and the document is issued with this time.'
  );
}

/** A refusal on any quote/SO read or write, as a sentence. */
export function quoteErrorText(error: unknown): string {
  if (error instanceof ApiTransportError) {
    return 'The server could not be reached. Nothing was changed.';
  }
  if (error instanceof ApiRequestError) {
    if (error.code === 'COUNTRY_SCOPE_VIOLATION') {
      return 'That document belongs to another country.';
    }
    if (error.status === 404) return 'That document does not exist.';
    if (error.status === 403) {
      // Deliberately NOT the API's wording. A 403 message can name the permission
      // key, and a key is a word for administrators — the person reading this
      // needs to know who to ask, not which string to grep for.
      return 'This account is not allowed to do that in this country.';
    }
    if (error.status === 409) {
      /*
       * The lifecycle's own conflicts, PASSED THROUGH VERBATIM: sending a quote
       * that is not a draft, confirming one that was never sent, deciding a
       * discount when none is pending, approving one raised against a version of
       * the quote that has since moved (U2's staleness check).
       *
       * Each of those already says which state it found — "Quote 41 has no
       * pending discount request to decide." — and appending a house sentence
       * like "reload to see where it is" would add words to a message that is
       * already complete, on the one class of error where the API's wording is
       * the most precise thing on screen.
       *
       * Save 33 c)'s, whose server wording names the mechanism (`confirm_clock`).
       */
      const clock = clockBehindOf(error);
      return clock === null ? error.message : clockBehindSentence(clock);
    }
    if (error.status === 429) {
      return 'Too many PDF renders in a short window. Wait a moment and try again.';
    }
    /*
     * THE ONE 422 THIS FILE TRANSLATES AGAIN — and it is the shape the header allows:
     * the server's `SAFT_FIELD_MISSING` sentence names the MECHANISM ("under the SAF-T(AO)
     * regime") and the operator needs the FIELDS, which arrive as a stable list in
     * `details.fields` (design U8 item 33 a); `assert-saft-fields.ts`). The sentence is built
     * from that list by `domain/saft-fields.ts` — the same vocabulary the builder shows
     * before the first request. Unlike the retired PRICE_UNVERIFIED rewrite, nothing here
     * points at a screen the server did not name, and nothing hides a field.
     */
    if (error.status === 422 && refusalReasonOf(error.details) === 'SAFT_FIELD_MISSING') {
      return saftFieldsSentence((error.details as { fields?: unknown }).fields);
    }
    return error.message;
  }
  return 'Something went wrong. Nothing was changed.';
}
